/**
 * The edits the Settings → UI toolbar panel makes, as pure functions.
 *
 * Every op starts from the EFFECTIVE toolbar (the merge of the stored layout
 * over the shipped default) rather than the stored array, so a user whose
 * customization predates a newly shipped button does not silently drop it the
 * first time they drag something. Every op ends by normalizing, so the anchor
 * policy holds no matter which entry point produced the edit — and an edit
 * that lands back on the shipped default returns `null`, the same value Reset
 * writes.
 */
import { UNHIDEABLE_TOOLBAR_ITEM_IDS, type ToolbarItemId } from './toolbarItemTypes';
import { TOOLBAR_HOME_GROUP } from './mergeToolbarLayout';
import {
  TOOLBAR_CUSTOMIZATION_VERSION,
  TOOLBAR_PRIMARY_CAP,
  normalizeToolbarCustomization,
  resolveToolbarBuckets,
  type ToolbarCustomization,
} from './toolbarCustomization';

const UNHIDEABLE = new Set<ToolbarItemId>(UNHIDEABLE_TOOLBAR_ITEM_IDS);

type Draft = { primary: ToolbarItemId[]; hidden: ToolbarItemId[] };

/** The effective toolbar, as an editable draft. */
function draft(current: ToolbarCustomization | null): Draft {
  const buckets = resolveToolbarBuckets(current);
  return { primary: [...buckets.primary], hidden: [...buckets.hidden] };
}

function commit(next: Draft): ToolbarCustomization | null {
  return normalizeToolbarCustomization({ version: TOOLBAR_CUSTOMIZATION_VERSION, ...next })
    .customization;
}

/** False once the row is full — the panel disables further promotion. */
export function canPromoteToolbarItem(current: ToolbarCustomization | null): boolean {
  return resolveToolbarBuckets(current).primary.length < TOOLBAR_PRIMARY_CAP;
}

/** Puts a More-menu (or hidden) item on the row, just before More ▾. */
export function promoteToolbarItem(
  current: ToolbarCustomization | null,
  id: ToolbarItemId,
): ToolbarCustomization | null {
  if (!canPromoteToolbarItem(current)) return current;
  const next = draft(current);
  if (next.primary.includes(id)) return current;
  next.hidden = next.hidden.filter((other) => other !== id);
  next.primary = [...next.primary.slice(0, -1), id, ...next.primary.slice(-1)];
  return commit(next);
}

/**
 * Takes an item off the row. It falls back to its More group when it has one;
 * a default row item (Terminal, AI ▾, …) has no group to fall back to, so
 * taking it off the row means hiding it — which is what the panel shows.
 */
export function demoteToolbarItem(
  current: ToolbarCustomization | null,
  id: ToolbarItemId,
): ToolbarCustomization | null {
  if (UNHIDEABLE.has(id)) return current;
  const next = draft(current);
  next.primary = next.primary.filter((other) => other !== id);
  if (!TOOLBAR_HOME_GROUP.has(id)) next.hidden = [...next.hidden, id];
  return commit(next);
}

/** Removes an item from the toolbar entirely — the row AND the More menu. */
export function hideToolbarItem(
  current: ToolbarCustomization | null,
  id: ToolbarItemId,
): ToolbarCustomization | null {
  if (UNHIDEABLE.has(id)) return current;
  const next = draft(current);
  next.primary = next.primary.filter((other) => other !== id);
  if (!next.hidden.includes(id)) next.hidden = [...next.hidden, id];
  return commit(next);
}

/**
 * False when restoring would push the row past the cap.
 *
 * Only an item with no home group lands back ON the row; everything else
 * returns to its More group and cannot overflow anything. Without this,
 * promote → promote → hide → promote → restore builds a 10-slot row, and
 * `normalize` deliberately never truncates.
 */
export function canRestoreToolbarItem(
  current: ToolbarCustomization | null,
  id: ToolbarItemId,
): boolean {
  return TOOLBAR_HOME_GROUP.has(id) || canPromoteToolbarItem(current);
}

/** Puts a hidden item back where the shipped default has it. */
export function restoreToolbarItem(
  current: ToolbarCustomization | null,
  id: ToolbarItemId,
): ToolbarCustomization | null {
  if (!canRestoreToolbarItem(current, id)) return current;
  const next = draft(current);
  next.hidden = next.hidden.filter((other) => other !== id);
  // Nothing to re-insert by hand: the merge puts an unplaced id back at its
  // default index, in the primary row or in its own group.
  return commit(next);
}

/** dnd-kit drop: move `activeId` to where `overId` sits. */
export function reorderToolbarPrimary(
  current: ToolbarCustomization | null,
  activeId: ToolbarItemId,
  overId: ToolbarItemId,
): ToolbarCustomization | null {
  if (activeId === overId) return current;
  const next = draft(current);
  const from = next.primary.indexOf(activeId);
  const to = next.primary.indexOf(overId);
  if (from < 0 || to < 0) return current;
  next.primary.splice(from, 1);
  next.primary.splice(to, 0, activeId);
  return commit(next);
}
